import axios from 'axios';
import * as SockJS from 'sockjs-client';
import { RxStomp } from '@stomp/rx-stomp';
import VueRouter from 'vue-router';
import { Store } from 'vuex';

import buildPaginationQueryOpts from '@/shared/sort/sorts';
import { AccountStateStorable } from '@/shared/config/store/account-store';
import { IChatMessage } from '@/shared/model/chat/chat-message.model';

const baseApiUrl = 'api/chat-messages';

let DESTINATION_NEW_CHAT_NEW_CHAT_MESSAGE = '/user/${employeeId}/topic/new-chat-new-chat-message';
let DESTINATION_NEW_CHAT_MESSAGE = '/user/${employeeId}/topic/new-chat-message';
let DESTINATION_FOR_CHAT_LIST_NEW_CHAT_MESSAGE = '/user/${employeeId}/topic/for-chat-list/new-chat-message';
let DESTINATION_LOOP_READ_CHAT_MESSAGE = '/user/${employeeId}/topic/loop-read-chat-message';
let DESTINATION_READ_CHAT_MESSAGE = '/user/${employeeId}/topic/read-chat-message';
let DESTINATION_SEND_APP_TYPING = '/app/send/typing';
let DESTINATION_TYPING = '/user/${employeeId}/topic/typing';
let DESTINATION_DELETE_CHAT_MESSAGE = '/user/${employeeId}/topic/delete-chat-message';
let DESTINATION_UPDATE_CHAT_MESSAGE = '/user/${employeeId}/topic/update-chat-message';
let DESTINATION_ADD_NEW_MEMBER_CHAT = '/user/${employeeId}/topic/add-new-member-chat';
let DESTINATION_UPDATE_CHAT = '/user/${employeeId}/topic/update-chat';
let DESTINATION_ADD_MEMBER_CHAT = '/user/${employeeId}/topic/add-member-chat';
let DESTINATION_REMOVE_MEMBER_CHAT = '/user/${employeeId}/topic/remove-member-chat';
let DESTINATION_LEAVE_CHAT = '/user/${employeeId}/topic/leave-chat';

export default class ChatMessageService {
  private rxStomp: RxStomp;

  constructor(private router: VueRouter, private store: Store<AccountStateStorable>) {
    // this.stomp = new RxStomp();
    // this.store.watch(
    //   (_state, getters) => getters.authenticated,
    //   (value, oldValue) => {
    //     if (value === oldValue) return;
    //     if (value) {
    //       return this.connect();
    //     }
    //     return this.disconnect();
    //   }
    // );
  }

  set stomp(rxStomp) {
    this.rxStomp = rxStomp;
    this.rxStomp.configure({
      connectHeaders: {
        name: 'X-XSRF-TOKEN',
        // value: document.cookie.match(new RegExp(`XSRF-TOKEN=([^;]+)`))[1],
      },
      debug: (msg: string): void => {
        console.log(new Date(), msg);
      },
    });
  }

  private updateCredentials(): void {
    this.rxStomp.configure({
      connectHeaders: {
        name: 'X-XSRF-TOKEN',
        // value: document.cookie.match(new RegExp(`XSRF-TOKEN=([^;]+)`))[1],
      },
      webSocketFactory: () => {
        return new SockJS(this.buildUrl());
      },
    });
  }

  public connect(): void {
    this.updateCredentials();
    return this.rxStomp.activate();
  }

  public disconnect(): Promise<void> {
    return this.rxStomp.deactivate();
  }

  private buildUrl(): string {
    // building absolute path so that websocket doesn't fail when deploying with a context path
    const loc = window.location;
    const baseHref = document.querySelector('base').getAttribute('href');
    // const url = '/websocket/chat';
    // const url = '//' + loc.host + baseHref + 'websocket/chat';
    const url = `${loc.protocol}//${loc.host}/websocket/chat`;
    const authToken = this.getAuthToken();
    if (authToken) {
      return `${url}?access_token=${authToken}`;
    }
    return url;
  }

  private getAuthToken() {
    const authToken = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
    return authToken;
  }

  public find(id: number): Promise<IChatMessage> {
    return new Promise<IChatMessage>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieve(paginationQuery?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(baseApiUrl + `?${buildPaginationQueryOpts(paginationQuery)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrievePaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public findAllPaging(paginationQuery: any, filter: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/find-all/paging?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public delete(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public deleteWidthChatId(chatId: number, id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/${chatId}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public create(entity: IChatMessage): Promise<IChatMessage> {
    return new Promise<IChatMessage>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public update(entity: IChatMessage): Promise<IChatMessage> {
    return new Promise<IChatMessage>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/${entity.id}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // public subscribeNewChatNewMessage(observer) {
  //   DESTINATION_NEW_CHAT_NEW_CHAT_MESSAGE = DESTINATION_NEW_CHAT_NEW_CHAT_MESSAGE.replace(
  //     '${employeeId}',
  //     this.store.getters.account.employeeId
  //   );
  //   return this.rxStomp.watch(DESTINATION_NEW_CHAT_NEW_CHAT_MESSAGE).subscribe(observer);
  // }
  //
  // public subscribeNewChatMessage(observer) {
  //   DESTINATION_NEW_CHAT_MESSAGE = DESTINATION_NEW_CHAT_MESSAGE.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_NEW_CHAT_MESSAGE).subscribe(observer);
  // }
  //
  // public subscribeForChatListNewMessage(observer) {
  //   DESTINATION_FOR_CHAT_LIST_NEW_CHAT_MESSAGE = DESTINATION_FOR_CHAT_LIST_NEW_CHAT_MESSAGE.replace(
  //     '${employeeId}',
  //     this.store.getters.account.employeeId
  //   );
  //   return this.rxStomp.watch(DESTINATION_FOR_CHAT_LIST_NEW_CHAT_MESSAGE).subscribe(observer);
  // }
  //
  // public subscribeLoopReadChatMessage(observer) {
  //   DESTINATION_LOOP_READ_CHAT_MESSAGE = DESTINATION_LOOP_READ_CHAT_MESSAGE.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_LOOP_READ_CHAT_MESSAGE).subscribe(observer);
  // }
  //
  // public subscribeReadChatMessage(observer) {
  //   DESTINATION_READ_CHAT_MESSAGE = DESTINATION_READ_CHAT_MESSAGE.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_READ_CHAT_MESSAGE).subscribe(observer);
  // }
  //
  // public sendTyping(chatId): void {
  //   this.rxStomp.publish({
  //     destination: DESTINATION_SEND_APP_TYPING,
  //     body: chatId,
  //   });
  // }
  //
  // public subscribeTyping(observer) {
  //   DESTINATION_TYPING = DESTINATION_TYPING.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_TYPING).subscribe(observer);
  // }
  //
  // public subscribeDeleteChatMessage(observer) {
  //   DESTINATION_DELETE_CHAT_MESSAGE = DESTINATION_DELETE_CHAT_MESSAGE.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_DELETE_CHAT_MESSAGE).subscribe(observer);
  // }
  //
  // public subscribeUpdateChatMessage(observer) {
  //   DESTINATION_UPDATE_CHAT_MESSAGE = DESTINATION_UPDATE_CHAT_MESSAGE.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_UPDATE_CHAT_MESSAGE).subscribe(observer);
  // }
  //
  // public subscribeAddNewMemberChat(observer) {
  //   DESTINATION_ADD_NEW_MEMBER_CHAT = DESTINATION_ADD_NEW_MEMBER_CHAT.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_ADD_NEW_MEMBER_CHAT).subscribe(observer);
  // }
  //
  // public subscribeUpdateChat(observer) {
  //   DESTINATION_UPDATE_CHAT = DESTINATION_UPDATE_CHAT.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_UPDATE_CHAT).subscribe(observer);
  // }
  //
  // public subscribeAddMemberChat(observer) {
  //   DESTINATION_ADD_MEMBER_CHAT = DESTINATION_ADD_MEMBER_CHAT.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_ADD_MEMBER_CHAT).subscribe(observer);
  // }
  //
  // public subscribeRemoveMemberChat(observer) {
  //   DESTINATION_REMOVE_MEMBER_CHAT = DESTINATION_REMOVE_MEMBER_CHAT.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_REMOVE_MEMBER_CHAT).subscribe(observer);
  // }
  //
  // public subscribeLeaveChat(observer) {
  //   DESTINATION_LEAVE_CHAT = DESTINATION_LEAVE_CHAT.replace('${employeeId}', this.store.getters.account.employeeId);
  //   return this.rxStomp.watch(DESTINATION_LEAVE_CHAT).subscribe(observer);
  // }
}
